'use client';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
    EntityCategoryItem,
    type EntityCategoryItemProps,
} from '@/ui-components/molecules/EntityCategoryItem/EntityCategoryItem';
import styles from './contentCategory.module.scss';
import { responsiveBreakpointsPx } from '@/constants';
import { ExploreAll } from '@/ui-components/molecules/ExploreAll/ExploreAll';
import { type ImageItem } from '@/ui-components/molecules/EntityCategoryImage/EntityCategoryImage';
import cn from 'classnames';

const {
    twoInRowGridStyles,
    threeInRowGridStyles,
    categoryItemStyles,
    leftMargin,
    rightMargin,
    listGridStyles,
    twoItemMaxStyles,
} = styles;

export enum ItemVariant {
    tile = 'tile',
    list = 'list',
}

export enum TileVariant {
    collection = 'collection',
    single = 'single',
}

export enum LayoutGrid {
    threeInRow = 'threeInRow',
    twoInRow = 'twoInRow',
}

export interface ContentCategoryProps {
    itemVariant: ItemVariant;
    layoutGrid: LayoutGrid;
    tileVariant: TileVariant;
    items: EntityCategoryItemProps[];
    isMultiple?: boolean;
    exploreAllBackgroundColor?: string;
    exploreAllLinkHref?: string;
}

export function ContentCategory({
    itemVariant,
    layoutGrid,
    items,
    tileVariant,
    isMultiple = false,
    exploreAllBackgroundColor,
    exploreAllLinkHref,
}: ContentCategoryProps) {
    let content = null;
    const viewportIsLargeSized = useMediaQuery(`(min-width: ${responsiveBreakpointsPx.lg}px)`);

    if (itemVariant === ItemVariant.tile && tileVariant === TileVariant.collection) {
        if (layoutGrid === LayoutGrid.twoInRow) {
            content = (
                <div className={twoInRowGridStyles}>
                    {items.map((item, index) => (
                        <EntityCategoryItem
                            key={index}
                            items={item.items}
                            variant={item.variant}
                            title={item.title}
                            description={item.description}
                            href={item.href}
                            squareImage={item.squareImage}
                            isImageSmall={item.isImageSmall}
                        />
                    ))}
                </div>
            );
        } else if (layoutGrid === LayoutGrid.threeInRow) {
            content = (
                <div className={threeInRowGridStyles}>
                    {items.map((item, index) => {
                        let calculatedStyles = categoryItemStyles;

                        if (items.length === 5) {
                            if (index === 3) {
                                calculatedStyles = `${categoryItemStyles} ${leftMargin}`;
                            } else if (index === 4) {
                                calculatedStyles = `${categoryItemStyles} ${rightMargin}`;
                            }
                        }

                        return (
                            <EntityCategoryItem
                                key={index}
                                items={item.items}
                                variant={item.variant}
                                title={item.title}
                                description={item.description}
                                href={item.href}
                                className={calculatedStyles}
                                squareImage={item.squareImage}
                                isImageSmall={item.isImageSmall}
                            />
                        );
                    })}
                </div>
            );
        }
    } else if (itemVariant === ItemVariant.tile && tileVariant === TileVariant.single) {
        if (layoutGrid === LayoutGrid.twoInRow) {
            content = (
                <div className={twoInRowGridStyles}>
                    {items.map((item, index) => (
                        <EntityCategoryItem
                            key={index}
                            items={item.items}
                            variant={item.variant}
                            title={item.title}
                            description={item.description}
                            href={item.href}
                            squareImage={item.squareImage}
                            isImageSmall={item.isImageSmall}
                        />
                    ))}
                </div>
            );
        } else if (layoutGrid === LayoutGrid.threeInRow) {
            content = (
                <div className={threeInRowGridStyles}>
                    {items.map((item, index) => {
                        let calculatedStyles = categoryItemStyles;

                        if (items.length === 5) {
                            if (index === 3) {
                                calculatedStyles = `${categoryItemStyles} ${leftMargin}`;
                            } else if (index === 4) {
                                calculatedStyles = `${categoryItemStyles} ${rightMargin}`;
                            }
                        }

                        return (
                            <EntityCategoryItem
                                key={index}
                                items={item.items}
                                variant={item.variant}
                                title={item.title}
                                description={item.description}
                                href={item.href}
                                className={calculatedStyles}
                                squareImage={item.squareImage}
                                isImageSmall={item.isImageSmall}
                            />
                        );
                    })}
                </div>
            );
        }
    } else if (itemVariant === ItemVariant.list) {
        const initialItems = viewportIsLargeSized ? items.slice(0, 3) : items.slice(0, 1);
        const imageItems: ImageItem[] = items.reduce((acc: ImageItem[], item: EntityCategoryItemProps) => {
            return acc.concat(item.items?.[0] ?? { image: <></> });
        }, []);
        if (isMultiple && (items.length >= 4 || (items.length >= 3 && !viewportIsLargeSized))) {
            content = (
                <div className={cn(listGridStyles, styles.isMultiple)}>
                    {initialItems.map((item, index) => (
                        <EntityCategoryItem
                            key={index}
                            items={item.items}
                            variant={item.variant}
                            title={item.title}
                            description={item.description}
                            href={item.href}
                            hideImage={item.hideImage}
                            squareImage={item.squareImage}
                            isImageSmall={item.isImageSmall}
                        />
                    ))}
                    <ExploreAll
                        items={imageItems}
                        title={'Explore all'}
                        href={exploreAllLinkHref ?? ''}
                        backgroundColor={exploreAllBackgroundColor}
                    />
                </div>
            );
        } else {
            content = (
                <div className={items.length < 3 ? twoItemMaxStyles : listGridStyles}>
                    {items.map((item, index) => (
                        <EntityCategoryItem
                            key={index}
                            items={item.items}
                            variant={item.variant}
                            title={item.title}
                            description={item.description}
                            href={item.href}
                            hideImage={item.hideImage}
                            squareImage={item.squareImage}
                            isImageSmall={item.isImageSmall}
                        />
                    ))}
                </div>
            );
        }
    }

    return content;
}
